import { API_URL } from "../config";
import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Check, ArrowRight, Shield, Zap, Crown } from 'lucide-react';
import axios from 'axios';
import { motion } from 'framer-motion';

const Pricing = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(null);

    const handleSubscribe = async (planType) => {
        if (!user) {
            navigate('/login');
            return;
        }

        setLoading(planType);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(`${API_URL}/payments/initiate`, 
                { planType },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            const { formParams, paytmUrl } = response.data;

            const form = document.createElement('form');
            form.method = 'POST';
            form.action = paytmUrl;

            for (const key in formParams) {
                const input = document.createElement('input');
                input.type = 'hidden';
                input.name = key;
                input.value = formParams[key];
                form.appendChild(input);
            }

            document.body.appendChild(form);
            form.submit();
            
        } catch (error) {
            console.error('Payment initiation failed:', error);
            alert('Failed to initiate payment. Please try again.');
            setLoading(null);
        }
    };

    const plans = [
        {
            id: 'basic_monthly',
            name: 'Basic',
            price: '150',
            duration: '/month',
            description: 'Perfect for getting started with your audience.',
            features: [
                'Up to 5 active link gateways',
                'Basic analytics tracking',
                'Standard theme templates',
                'Telegram app integration'
            ],
            style: {
                accent: 'from-blue-500 to-cyan-400',
                bg: 'bg-[#0f1524]',
                border: 'border-blue-500/20',
                hoverBorder: 'hover:border-blue-500/40',
                iconBg: 'bg-blue-500/10',
                iconColor: 'text-blue-400',
                button: 'bg-white/5 hover:bg-white/10 text-white border border-white/10'
            },
            icon: <Zap className="w-6 h-6 text-blue-400" />
        },
        {
            id: 'premium_monthly',
            name: 'Pro',
            price: '299',
            duration: '/month',
            description: 'Everything you need to scale and customize.',
            features: [
                'Unlimited active link gateways',
                'Advanced analytics & insights',
                'Custom themes & backgrounds',
                'Remove Linkey branding',
                'Priority email support'
            ],
            style: {
                accent: 'from-indigo-500 to-purple-500',
                bg: 'bg-[#111328]',
                border: 'border-indigo-500/30',
                hoverBorder: 'hover:border-indigo-500/50',
                iconBg: 'bg-indigo-500/10',
                iconColor: 'text-indigo-400',
                button: 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white shadow-lg shadow-indigo-500/25',
                glow: 'shadow-[0_0_30px_rgba(99,102,241,0.15)]'
            },
            icon: <Crown className="w-6 h-6 text-indigo-400" />,
            popular: true
        },
        {
            id: 'premium_yearly',
            name: 'Max',
            price: '1999',
            duration: '/year',
            description: 'The ultimate package for serious creators.',
            features: [
                'Unlimited active link gateways',
                'Advanced analytics & insights',
                'Custom themes & backgrounds',
                'Remove Linkey branding',
                '24/7 Priority support',
                'Save ₹1589 annually'
            ],
            style: {
                accent: 'from-yellow-400 to-amber-600',
                bg: 'bg-[#151310]',
                border: 'border-yellow-500/20',
                hoverBorder: 'hover:border-yellow-500/40',
                iconBg: 'bg-yellow-500/10',
                iconColor: 'text-yellow-400',
                button: 'bg-white/5 hover:bg-white/10 text-white border border-white/10'
            },
            icon: <Shield className="w-6 h-6 text-yellow-400" />
        }
    ];

    return (
        <div className="min-h-screen bg-black text-white py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
            {}
            <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-indigo-600/20 rounded-full blur-[120px] pointer-events-none" />
            <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-purple-600/10 rounded-full blur-[120px] pointer-events-none" />

            <div className="max-w-7xl mx-auto relative z-10">
                <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="text-center max-w-3xl mx-auto mb-20"
                >
                    <h1 className="text-5xl md:text-6xl font-extrabold mb-6 tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-white to-slate-400">
                        Unlock your potential
                    </h1>
                    <p className="text-xl text-slate-400 leading-relaxed">
                        Choose the perfect gateway plan to grow your audience and protect your premium content securely.
                    </p>
                </motion.div>

                <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                    {plans.map((plan, index) => (
                        <motion.div 
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: index * 0.1 }}
                            key={plan.id} 
                            className={`relative flex flex-col p-1 rounded-3xl ${plan.style.bg} ${plan.style.glow || ''} transition-all duration-300 ${plan.popular ? 'md:-mt-6 md:mb-6' : ''}`}
                        >
                            {}
                            <div className={`absolute inset-0 rounded-3xl bg-gradient-to-br ${plan.style.accent} opacity-20 pointer-events-none`} />
                            
                            <div className={`relative h-full flex flex-col bg-[#0b101e]/90 backdrop-blur-xl rounded-[23px] p-8 border ${plan.style.border} ${plan.style.hoverBorder} transition-colors`}>
                                {plan.popular && (
                                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                                        <div className="relative">
                                            <div className="absolute inset-0 bg-indigo-500 blur-md opacity-50" />
                                            <span className="relative bg-gradient-to-r from-indigo-500 to-purple-500 text-white text-xs font-black px-4 py-1.5 rounded-full uppercase tracking-wider shadow-lg">
                                                Most Popular
                                            </span>
                                        </div>
                                    </div>
                                )}
                                
                                <div className="mb-8">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className={`p-2.5 rounded-xl ${plan.style.iconBg}`}>
                                            {plan.icon}
                                        </div>
                                        <h3 className="text-2xl font-bold text-white tracking-tight">{plan.name}</h3>
                                    </div>
                                    <p className="text-slate-400 text-sm h-10 leading-relaxed">{plan.description}</p>
                                    <div className="mt-6 flex items-baseline">
                                        <span className="text-3xl font-bold text-slate-300 mr-1">₹</span>
                                        <span className="text-5xl font-extrabold text-white tracking-tighter">{plan.price}</span>
                                        <span className="ml-2 text-lg font-medium text-slate-500">{plan.duration}</span>
                                    </div>
                                </div>
                                
                                <ul className="flex-1 space-y-4 mb-10">
                                    {plan.features.map((feature, idx) => (
                                        <li key={idx} className="flex items-start">
                                            <div className={`flex-shrink-0 flex items-center justify-center w-5 h-5 rounded-full ${plan.style.iconBg} mt-0.5 mr-3`}>
                                                <Check className={`w-3.5 h-3.5 ${plan.style.iconColor}`} />
                                            </div>
                                            <span className="text-slate-300 text-sm font-medium">{feature}</span>
                                        </li>
                                    ))}
                                </ul>

                                <button 
                                    onClick={() => handleSubscribe(plan.id)}
                                    disabled={loading === plan.id}
                                    className={`w-full py-4 px-6 rounded-2xl font-bold text-sm transition-all duration-300 flex items-center justify-center gap-2 group
                                        ${plan.style.button}
                                        ${loading === plan.id ? 'opacity-75 cursor-not-allowed' : 'active:scale-95'}
                                    `}
                                >
                                    {loading === plan.id ? 'Processing...' : 'Get Started'}
                                    {!loading && <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />}
                                </button>
                            </div>
                        </motion.div>
                    ))}
                </div>

                <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.6 }}
                    className="mt-20 flex flex-col items-center justify-center gap-3 text-slate-500 text-sm font-medium"
                >
                    <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-emerald-500/70" /> 
                        <span>Bank-grade encryption for all transactions</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-600">
                        Securely processed by Paytm Gateway
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default Pricing;
